import cx from 'classnames';
import { useCallback, useMemo, useState, type FunctionComponent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CrossIcon from '@atlaskit/icon/core/cross';
import LikeIcon from '@atlaskit/icon/core/thumbs-up';
import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';
import { Analytics } from '@trello/atlassian-analytics';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import { useFeedbackCollector as useJiraFeedbackCollector } from '@trello/feedback-collector';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Tooltip } from '@trello/nachos/tooltip';

import { type FeatureNames, FeedbackFeatures } from './featuresInfo';
import type { OnFeedbackGiven } from './feedbackCollectorSharedState';
import { NegativeFeedbackDetail } from './NegativeFeedbackDetail';
import { useFeedbackCollector } from './useFeedbackCollector';

import * as styles from './FeedbackCollector.module.less';

export interface FeedbackCollectorProps {
  featureName: FeatureNames;
  flagTreatment?: boolean;
  onFeedbackGiven?: OnFeedbackGiven;
  onFeedbackCollectorClose?: () => void;
  /**
   * Extra per-instance context merged into the feedback events and the Jira
   * submission's context field, alongside the feature's own static
   * attributes (e.g. which category of a multi-category feature this
   * feedback is about).
   */
  attributes?: Record<string, string>;
}

export const FeedbackCollector: FunctionComponent<FeedbackCollectorProps> = ({
  featureName,
  flagTreatment,
  onFeedbackGiven,
  onFeedbackCollectorClose,
  attributes,
}) => {
  const feature = FeedbackFeatures[featureName];
  const intl = useIntl();
  const flagSubtitle = feature.flagSubtitle(intl);
  const isAiBoardBuilder =
    featureName === 'aiBoardBuilder' ||
    featureName === 'aiBoardBuilderWithCommandCenter';
  const { cohort: aibbM2Cohort } = useGetExperimentValue({
    experimentName: 'trello_goo_aibb_m2',
    parameter: 'cohort',
    fireExposureEvent: false,
  });
  const [shouldShowFeedbackDetail, setShouldShowFeedbackDetail] =
    useState(false);
  const [selectedThumbButton, setSelectedThumbButton] = useState<
    'down' | 'up' | null
  >(null);
  const [wasFeedbackSubmitted, setWasFeedbackSubmitted] =
    useState<boolean>(false);

  const {
    getFeedbackCollectorProjectKey,
    isOpen,
    dismissFeedbackCollector,
    traceId,
    negativeFacets,
  } = useFeedbackCollector({
    featureName,
  });

  const JOIN_SESSION_LINK =
    'https://www.userinterviews.com/projects/-d5Sihhzyw/apply';

  const jiraAttributes = useMemo(
    () => ({
      ...attributes,
      // Only board builder triage cares about the M2 cohort.
      ...(isAiBoardBuilder ? { aibbM2Cohort } : {}),
    }),
    [aibbM2Cohort, attributes, isAiBoardBuilder],
  );

  /*
   * The context override replaces the whole Feedback Context field, so the
   * attributes have to be folded into it — otherwise they'd reach the
   * analytics events below but never the submitted ticket.
   */
  const getFeedbackContextOverride = useCallback(
    () =>
      Object.entries(jiraAttributes).reduce(
        (context, [key, value]) => `${context}, ${key}: ${value}`,
        getFeedbackCollectorProjectKey(),
      ),
    [getFeedbackCollectorProjectKey, jiraAttributes],
  );

  const entrypointId = feature.entrypoint();

  const isVoiceCaptureFlag = flagTreatment && featureName === 'voiceCapture';

  const handleJiraFeedbackCollectorClose = useCallback(() => {
    if (flagTreatment) {
      dismissFeedbackCollector();
    }
    onFeedbackCollectorClose?.();
  }, [dismissFeedbackCollector, flagTreatment, onFeedbackCollectorClose]);

  const {
    FeedbackCollector: JiraFeedbackCollector,
    showFeedbackCollector: showJiraFeedbackCollector,
  } = useJiraFeedbackCollector({
    entrypointId,
    source: 'aiLabsFeedbackCollector',
    onClose: handleJiraFeedbackCollectorClose,
    onSubmit: handleJiraFeedbackCollectorClose,
    attributes: jiraAttributes,
    feedbackCollectorProps: {
      feedbackTitle: feature.dialogTitle(intl),
      // TODO Hoist upwards to clients
      customContent: isAiBoardBuilder ? (
        <SectionMessage
          appearance="information"
          title={intl.formatMessage({
            id: 'templates.ai_board_builder.feedback-section-message-title',
            defaultMessage: 'Your feedback = better Trello',
            description:
              'Title for the AI Board Builder feedback session invitation section message',
          })}
          headingLevel={'h3'}
          actions={[
            <SectionMessageAction
              href={JOIN_SESSION_LINK}
              key="join-session-link"
              target="_blank"
            >
              <FormattedMessage
                id="templates.ai_board_builder.feedback-section-message-action"
                defaultMessage="Join a session"
                description="Action link in the AI Board Builder feedback session invitation"
              />
            </SectionMessageAction>,
          ]}
        >
          <FormattedMessage
            id="templates.ai_board_builder.feedback-section-message-body"
            defaultMessage="Join a 30-minute session to share feedback and shape what's next."
            description="Body text inviting users to a 30-minute AI Board Builder feedback session"
          />
        </SectionMessage>
      ) : undefined,
    },
    getFeedbackContextOverride,
  });

  const handleFeedbackSubmitted = useCallback(() => {
    if (flagTreatment) {
      dismissFeedbackCollector();

      showFlag({
        id: 'aiLabsFeedbackCollectorSuccessMessage',
        title: intl.formatMessage({
          id: 'templates.ai-labs.feedback-thank-you',
          defaultMessage: 'Thank you for your feedback!',
          description: 'Success message after submitting feedback',
        }),
        appearance: 'success',
        isAutoDismiss: true,
        msTimeout: 2000,
      });
    }
    setWasFeedbackSubmitted(true);
  }, [flagTreatment, dismissFeedbackCollector, intl]);

  const handleThumbsUp = useCallback(() => {
    setShouldShowFeedbackDetail(false);
    setSelectedThumbButton(selectedThumbButton === 'up' ? null : 'up');

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'aiLabsFeedbackCollectorThumbsUpButton',
      source: 'aiLabsFeedbackCollector',
      attributes: {
        ...attributes,
        aiLabsFeatureName: featureName,
        aiLabsSentimentModifier: 1, // Assuming positive sentiment for thumbs up
        taskId: traceId,
      },
    });

    if (flagTreatment && !isVoiceCaptureFlag) {
      dismissFeedbackCollector();

      showFlag({
        id: 'aiLabsFeedbackCollectorSuccessMessage',
        title: intl.formatMessage({
          id: 'templates.ai-labs.feedback-thank-you',
          defaultMessage: 'Thank you for your feedback!',
          description: 'Success message after submitting feedback',
        }),
        appearance: 'success',
        isAutoDismiss: true,
        msTimeout: 2000,
      });
    } else {
      showJiraFeedbackCollector();
    }
    if (!isVoiceCaptureFlag) {
      setWasFeedbackSubmitted(true);
    }
    onFeedbackGiven?.('up', traceId);
  }, [
    selectedThumbButton,
    featureName,
    traceId,
    flagTreatment,
    dismissFeedbackCollector,
    showJiraFeedbackCollector,
    intl,
    isVoiceCaptureFlag,
    onFeedbackGiven,
    attributes,
  ]);

  const handleThumbsDown = useCallback(() => {
    setSelectedThumbButton(selectedThumbButton === 'down' ? null : 'down');

    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'aiLabsFeedbackCollectorThumbsDownButton',
      source: 'aiLabsFeedbackCollector',
      attributes: {
        ...attributes,
        aiLabsFeatureName: featureName,
        aiLabsSentimentModifier: -1, // Assuming negative sentiment for thumbs down
        taskId: traceId,
      },
    });
    if (flagTreatment && !isVoiceCaptureFlag && negativeFacets.length > 0) {
      setShouldShowFeedbackDetail(true);
    } else if (flagTreatment && !isVoiceCaptureFlag) {
      // No facets to pick a reason from — open the Jira collector directly,
      // same as the "Provide detailed feedback" link, instead of opening an
      // empty detail view or silently completing with a thank-you flag.
      showJiraFeedbackCollector();
    } else {
      if (!isVoiceCaptureFlag) {
        setWasFeedbackSubmitted(true);
      }
      showJiraFeedbackCollector();
    }
    onFeedbackGiven?.('down', traceId);
  }, [
    featureName,
    selectedThumbButton,
    traceId,
    flagTreatment,
    showJiraFeedbackCollector,
    isVoiceCaptureFlag,
    onFeedbackGiven,
    attributes,
    negativeFacets,
  ]);

  if (flagTreatment && !isOpen) {
    return null;
  }

  // TODO Hoist feature-specific content upwards to client
  const feedbackCollectorContents = (
    <>
      <div data-ai-feature={featureName}>
        <JiraFeedbackCollector />
      </div>
      <div className={styles.feedbackButtonsContainer}>
        {flagTreatment && (
          <div className={styles.titleAndCloseButton}>
            {isAiBoardBuilder ? (
              <div className={styles.titleSection}>
                <span className={styles.mainTitle}>
                  <FormattedMessage
                    id="templates.ai_board_builder.feedback-popup-title"
                    defaultMessage="Your board is ready!"
                    description="Title for the AI Board Builder feedback popup"
                  />
                </span>
                <span>
                  <FormattedMessage
                    id="templates.ai_board_builder.feedback-popup-subtitle"
                    defaultMessage="How did we do?"
                    description="Subtitle for the AI Board Builder feedback popup"
                  />
                </span>
              </div>
            ) : flagSubtitle ? (
              <div className={styles.titleSection}>
                <span className={styles.mainTitle}>
                  {feature.flagTitle(intl)}
                </span>
                <span>{flagSubtitle}</span>
              </div>
            ) : (
              <span>{feature.flagTitle(intl)}</span>
            )}
            <Button
              className={styles.closeButton}
              onClick={dismissFeedbackCollector}
              appearance="subtle"
            >
              <CrossIcon
                size="small"
                label={intl.formatMessage({
                  id: 'templates.ai-labs.feedback-close-label',
                  defaultMessage: 'Close feedback collector',
                  description: 'Accessibility label for close button',
                })}
              />
            </Button>
          </div>
        )}
        <div className={styles.thumbButtonGroup}>
          {featureName === 'aiCardMerge' ? (
            <>
              <Tooltip
                content={intl.formatMessage({
                  id: 'templates.ai-labs.card-merge-feedback-valuable',
                  defaultMessage: 'This card merge was valuable',
                  description: 'Tooltip for card merge thumbs up button',
                })}
                tag="span"
                position="top"
              >
                <Button
                  onClick={handleThumbsUp}
                  appearance="subtle"
                  className={cx(styles.positiveFeedbackButton, {
                    [styles.selected]: selectedThumbButton === 'up',
                  })}
                >
                  <LikeIcon
                    label={intl.formatMessage({
                      id: 'templates.ai-labs.card-merge-feedback-valuable',
                      defaultMessage: 'This card merge was valuable',
                      description:
                        'A thumbs up icon indicating the card merge was valuable',
                    })}
                  />
                </Button>
              </Tooltip>
              <Tooltip
                content={intl.formatMessage({
                  id: 'templates.ai-labs.card-merge-feedback-not-valuable',
                  defaultMessage: 'This card merge was not valuable',
                  description: 'Tooltip for card merge thumbs down button',
                })}
                tag="span"
                position="top"
              >
                <Button
                  onClick={handleThumbsDown}
                  appearance="subtle"
                  className={cx(styles.negativeFeedbackButton, {
                    [styles.selected]: selectedThumbButton === 'down',
                  })}
                >
                  <LikeIcon
                    label={intl.formatMessage({
                      id: 'templates.ai-labs.card-merge-feedback-not-valuable',
                      defaultMessage: 'This card merge was not valuable',
                      description:
                        'A thumbs down icon indicating the card merge was not valuable',
                    })}
                  />
                </Button>
              </Tooltip>
            </>
          ) : featureName === 'aiCardSummary' ? (
            <>
              <Tooltip
                content={intl.formatMessage({
                  id: 'templates.ai-labs.card-summary-feedback-valuable',
                  defaultMessage: 'This summary was valuable',
                  description: 'Tooltip for card summary thumbs up button',
                })}
                tag="span"
                position="top"
              >
                <Button
                  onClick={handleThumbsUp}
                  appearance="subtle"
                  className={cx(styles.positiveFeedbackButton, {
                    [styles.selected]: selectedThumbButton === 'up',
                  })}
                >
                  <LikeIcon
                    label={intl.formatMessage({
                      id: 'templates.ai-labs.card-summary-feedback-valuable',
                      defaultMessage: 'This summary was valuable',
                      description:
                        'A thumbs up icon indicating the card summary was valuable',
                    })}
                  />
                </Button>
              </Tooltip>
              <Tooltip
                content={intl.formatMessage({
                  id: 'templates.ai-labs.card-summary-feedback-not-valuable',
                  defaultMessage: 'This summary was not valuable',
                  description: 'Tooltip for card summary thumbs down button',
                })}
                tag="span"
                position="top"
              >
                <Button
                  onClick={handleThumbsDown}
                  appearance="subtle"
                  className={cx(styles.negativeFeedbackButton, {
                    [styles.selected]: selectedThumbButton === 'down',
                  })}
                >
                  <LikeIcon
                    label={intl.formatMessage({
                      id: 'templates.ai-labs.card-summary-feedback-not-valuable',
                      defaultMessage: 'This summary was not valuable',
                      description:
                        'A thumbs down icon indicating the card summary was not valuable',
                    })}
                  />
                </Button>
              </Tooltip>
            </>
          ) : (
            <>
              <Button
                onClick={handleThumbsUp}
                appearance="subtle"
                className={cx(styles.positiveFeedbackButton, {
                  [styles.selected]: selectedThumbButton === 'up',
                })}
              >
                <LikeIcon
                  label={intl.formatMessage({
                    id: 'templates.ai-labs.feedback-valuable-label',
                    defaultMessage: 'Valuable',
                    description: 'Accessibility label for thumbs up button',
                  })}
                />
              </Button>
              <Button
                onClick={handleThumbsDown}
                appearance="subtle"
                className={cx(styles.negativeFeedbackButton, {
                  [styles.selected]: selectedThumbButton === 'down',
                })}
              >
                <LikeIcon
                  label={intl.formatMessage({
                    id: 'templates.ai-labs.feedback-not-valuable-label',
                    defaultMessage: 'Not valuable',
                    description: 'Accessibility label for thumbs down button',
                  })}
                />
              </Button>
            </>
          )}
        </div>
        {flagTreatment && !isVoiceCaptureFlag && (
          <>
            <Button onClick={showJiraFeedbackCollector} appearance="link">
              {isAiBoardBuilder ? (
                <FormattedMessage
                  id="templates.ai_board_builder.feedback-popup-link"
                  defaultMessage="Leave detailed feedback"
                  description="Link text to open the AI Board Builder detailed feedback modal"
                />
              ) : (
                <FormattedMessage
                  id="templates.ai-labs.feedback-collector-popup-link"
                  defaultMessage="Provide detailed feedback"
                  description="Link text to open detailed feedback modal"
                />
              )}
            </Button>
            {isAiBoardBuilder && (
              <p className={styles.joinSessionLink}>
                <a
                  href={JOIN_SESSION_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FormattedMessage
                    id="templates.ai_board_builder.feedback-join-session-link"
                    defaultMessage="Join a 30-minute feedback session"
                    description="Link inviting users to join a 30-minute AI Board Builder feedback session"
                  />
                </a>
              </p>
            )}
          </>
        )}
      </div>

      {shouldShowFeedbackDetail && (
        <NegativeFeedbackDetail
          featureName={featureName}
          traceId={traceId}
          feedbackSubmissionCallback={handleFeedbackSubmitted}
          flagTreatment={flagTreatment}
          attributes={attributes}
        />
      )}
    </>
  );

  const thankYouMessage = (
    <p>
      <FormattedMessage
        id="templates.ai-labs.feedback-thank-you"
        defaultMessage="Thank you for your feedback!"
        description="Success message after submitting feedback"
      />
    </p>
  );

  if (!flagTreatment && wasFeedbackSubmitted) {
    return <JiraFeedbackCollector />;
  }

  return (
    <div
      className={cx(styles.feedbackCollectorContainer, {
        [styles.flagTreatment]: flagTreatment,
      })}
    >
      {!wasFeedbackSubmitted && feedbackCollectorContents}
      {wasFeedbackSubmitted && flagTreatment && thankYouMessage}
    </div>
  );
};
